import * as React from "react";
import { useSearchParams } from "react-router-dom";

import { PRIVACY_POLICY_URL, TERMS_OF_SERVICE_URL } from "@/lib/legalLinks";
import logoHorizontal from "@/assets/brand/scheda-logo-horizontal.svg";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { AuthModal } from "@/features/auth/AuthModal";
import "./landing-redesign.css";
import { SchedaChat } from "./SchedaChat";
import { SchedaOrigin } from "./SchedaOrigin";

/**
 * The unauthenticated Scheda landing — autumn-metal variant, built to the approved preview
 * (mission control, design/home-scheda.html). Six bands: origin → product → how it works →
 * pricing (with its FAQ) → security → closing, then footer and cookie notice. The page opens
 * on the origin band and has no hero. Rendered by {@link AuthGate} as the logged-out home of
 * scheda.delgentic.com.
 *
 * <p>Every band names its own ground and metal rule as classes (.band.ochre.rule-brass …)
 * instead of the old `section:nth-of-type(odd|even)` alternation, so the section count is no
 * longer load-bearing.
 *
 * <p>CTAs collapse to one door: a single Log in in the nav, one in the origin band, and the
 * closing keeps its primary action. Pricing keeps its four tier CTAs.
 *
 * <p>Sign in / sign up happen in the {@link AuthModal} — a metallic card over the landing —
 * rather than a page hop. Its visibility is carried in the {@code ?auth=signin|signup} search
 * param so Esc, the ←/backdrop, and the browser Back button all dismiss it; {@code autoOpenAuth}
 * opens it straight away when the visitor arrived to sign in (a gated deep link), and
 * {@code from} is where a successful sign-in lands them. The full {@code /signin} etc. pages
 * still exist for email-link and deep-link flows.
 *
 * <p>Everything is scoped under `.lp` (see landing-redesign.css) so the palette and generic
 * class names (.band, .btn…) never leak into the app. Pricing limits are hardcoded here for
 * now (an admin-editable pricing source is a separate follow-up).
 */

/** Security-assurance cards, laid out in the masonry. Accents walk blue → moss → rust. */
const SECURITY: { ic: string; accent: "blue" | "teal" | "coral"; title: string; body: string }[] = [
  { ic: "🏅", accent: "blue", title: "Held to real standards", body: "Recognised best practices, reviewed as we grow." },
  { ic: "☁️", accent: "teal", title: "Runs on trusted cloud", body: "Hosted on AWS." },
  { ic: "🔑", accent: "coral", title: "You see only your data", body: "Tied to your account; invitees see only shared times." },
  { ic: "🔒", accent: "blue", title: "Locked in transit", body: "HTTPS-encrypted in motion." },
  { ic: "🗄️", accent: "teal", title: "Locked when stored", body: "Encrypted where it's saved." },
  { ic: "🧱", accent: "coral", title: "Shielded network", body: "Data kept behind layered protection, off the open internet." },
  { ic: "🤐", accent: "blue", title: "Kept confidential", body: "We don't sell or pass around your info." },
  { ic: "👥", accent: "teal", title: "Access on a need-to basis", body: "Staff access limited to what their work needs." },
  { ic: "🔗", accent: "coral", title: "Careful with partners", body: "Vetted providers, share only what a feature needs." },
  { ic: "🏢", accent: "blue", title: "Security, company-wide", body: "Built into how we plan, build and run Scheda." },
];

/** Reveal `.float` elements as they scroll into view (approximates reactbits scroll-float). */
function useScrollFloat(): void {
  React.useEffect(() => {
    const els = Array.from(document.querySelectorAll<HTMLElement>(".lp .float"));
    if (!("IntersectionObserver" in window)) {
      els.forEach((el) => el.classList.add("in"));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add("in")),
      { threshold: 0.15 },
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
}

/**
 * The three ways, moving: one card on screen at a time, dots and two arrows to move it,
 * auto-advancing every 3.4s — the same beat as the harness carousel on the apex. It pauses
 * on hover, on keyboard focus inside the carousel, and whenever the tab is hidden, and a
 * manual move restarts the clock rather than racing it. Under prefers-reduced-motion it
 * never advances on its own.
 */
function WaysCarousel({ labels, children }: { labels: string[]; children: React.ReactNode[] }) {
  const [index, setIndex] = React.useState(0);
  const count = children.length;
  const rootRef = React.useRef<HTMLDivElement>(null);
  const timer = React.useRef<number | null>(null);
  const indexRef = React.useRef(0);
  indexRef.current = index;

  const go = React.useCallback(
    (n: number) => setIndex(((n % count) + count) % count),
    [count],
  );

  const stop = React.useCallback(() => {
    if (timer.current !== null) {
      window.clearInterval(timer.current);
      timer.current = null;
    }
  }, []);
  const start = React.useCallback(() => {
    stop();
    const reduce =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return;
    timer.current = window.setInterval(() => go(indexRef.current + 1), 3400);
  }, [go, stop]);

  React.useEffect(() => {
    start();
    const onVisibility = (): void => {
      if (document.hidden) stop();
      else start();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [start, stop]);

  const onKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === "ArrowRight") {
      e.preventDefault();
      go(index + 1);
      start();
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      go(index - 1);
      start();
    } else if (e.key === "Home") {
      e.preventDefault();
      go(0);
      start();
    } else if (e.key === "End") {
      e.preventDefault();
      go(count - 1);
      start();
    }
  };

  return (
    <div
      className="ways"
      ref={rootRef}
      role="group"
      aria-label="Three ways to get booked"
      tabIndex={0}
      onKeyDown={onKeyDown}
      onMouseEnter={stop}
      onMouseLeave={start}
      onFocus={stop}
      onBlur={(e) => {
        if (!rootRef.current?.contains(e.relatedTarget as Node)) start();
      }}
    >
      <div className="carviewport">
        {children.map((slide, k) => (
          <div
            key={labels[k]}
            className={k === index ? "offer slide on" : "offer slide"}
            aria-hidden={k === index ? "false" : "true"}
          >
            {slide}
          </div>
        ))}
      </div>
      <div className="carnav">
        <button
          className="carbtn"
          type="button"
          aria-label="Previous"
          onClick={() => {
            go(index - 1);
            start();
          }}
        >
          ‹
        </button>
        <div className="cardots">
          {labels.map((label, k) => (
            <button
              key={label}
              type="button"
              className={k === index ? "cardot on" : "cardot"}
              aria-current={k === index ? "true" : "false"}
              onClick={() => {
                go(k);
                start();
              }}
            >
              {label}
            </button>
          ))}
        </div>
        <button
          className="carbtn"
          type="button"
          aria-label="Next"
          onClick={() => {
            go(index + 1);
            start();
          }}
        >
          ›
        </button>
      </div>
    </div>
  );
}

/** Cookie notice — remembered in localStorage so it shows once. UI only for now (no analytics wired). */
function CookieConsent() {
  const [show, setShow] = React.useState(false);
  React.useEffect(() => {
    try {
      if (!localStorage.getItem("scheda.cookieConsent")) setShow(true);
    } catch {
      setShow(true);
    }
  }, []);
  if (!show) return null;
  const done = (choice: string): void => {
    try {
      localStorage.setItem("scheda.cookieConsent", choice);
    } catch {
      /* storage blocked — just close */
    }
    setShow(false);
  };
  return (
    <div className="cookie">
      <p>
        We use cookies for essentials and, if you allow, analytics.{" "}
        <a href="/cookies" style={{ color: "var(--blue-ink)", fontWeight: 700 }}>
          Cookie policy
        </a>
        .
      </p>
      <div className="row">
        <button type="button" className="btn ghost" onClick={() => done("necessary")}>
          Decline unnecessary
        </button>
        <button type="button" className="btn cta" onClick={() => done("all")}>
          Allow all
        </button>
      </div>
    </div>
  );
}

/** One comparison-table cell: "yes" → check, "no" → dash, anything else rendered as text. */
function Cell({ v }: { v: string }) {
  if (v === "yes") return <span className="yes">✓</span>;
  if (v === "no") return <span className="no">–</span>;
  return <>{v}</>;
}

/**
 * A tier cell that leads with the number a buyer is comparing and lists what qualifies it
 * underneath, so a limit and its conditions stay in one cell instead of spreading over a row each.
 */
function Tier({ v, notes }: { v: string; notes?: string[] }) {
  return (
    <>
      <div className="tv">{v}</div>
      {notes?.map((n) => (
        <div className="tn" key={n}>{n}</div>
      ))}
    </>
  );
}

/**
 * A masonry — items flow into equal-width columns, kept in order via round-robin so the
 * layout stays balanced and reads left→right. Column count is responsive. Each item (marked
 * `.msi` by the render fn) rises in from below as it scrolls into view, staggered by index.
 */
function Masonry<T>({
  items,
  render,
  className,
}: {
  items: T[];
  render: (item: T, index: number) => React.ReactNode;
  className?: string;
}) {
  const [cols, setCols] = React.useState(5);
  const rootRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    const pick = (): void => {
      const w = window.innerWidth;
      setCols(w >= 1040 ? 5 : w >= 760 ? 3 : w >= 480 ? 2 : 1);
    };
    pick();
    window.addEventListener("resize", pick);
    return () => window.removeEventListener("resize", pick);
  }, []);
  // Rise-in on scroll: reveal each card once it enters view (re-runs when the columns re-flow).
  React.useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const cards = Array.from(root.querySelectorAll<HTMLElement>(".msi"));
    if (!("IntersectionObserver" in window)) {
      cards.forEach((c) => c.classList.add("in"));
      return;
    }
    const io = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("in");
            io.unobserve(e.target);
          }
        }),
      { threshold: 0.12 },
    );
    cards.forEach((c) => io.observe(c));
    return () => io.disconnect();
  }, [cols, items]);
  const columns: { item: T; idx: number }[][] = Array.from({ length: cols }, () => []);
  items.forEach((item, idx) => columns[idx % cols].push({ item, idx }));
  return (
    <div ref={rootRef} className={className ? `masonry ${className}` : "masonry"}>
      {columns.map((col, c) => (
        <div className="mcol" key={c}>
          {col.map(({ item, idx }) => (
            <React.Fragment key={idx}>{render(item, idx)}</React.Fragment>
          ))}
        </div>
      ))}
    </div>
  );
}

export function LandingPage({
  from = "/",
  autoOpenAuth = false,
}: {
  /** Where to land after a successful sign-in (the deep link the visitor was headed to). */
  from?: string;
  /** Open the sign-in modal immediately (the visitor arrived to sign in, e.g. a gated deep link). */
  autoOpenAuth?: boolean;
}) {
  useScrollFloat();

  // Sign-in / sign-up shows as a modal over the landing, its visibility carried in the URL
  // (?auth=signin|signup) so the browser Back button — and Esc / ← / backdrop — all dismiss it.
  const [params, setParams] = useSearchParams();
  const rawAuth = params.get("auth");
  const authMode = rawAuth === "signup" ? "signup" : rawAuth === "signin" ? "signin" : null;
  const openAuth = React.useCallback(
    (mode: "signin" | "signup") =>
      setParams((prev) => {
        prev.set("auth", mode);
        return prev;
      }),
    [setParams],
  );
  const closeAuth = React.useCallback(
    () =>
      setParams(
        (prev) => {
          prev.delete("auth");
          return prev;
        },
        { replace: true },
      ),
    [setParams],
  );

  // A visitor sent here specifically to sign in (a gated deep link) gets the modal straight away,
  // so it's still one click; a direct visitor just sees the landing.
  const autoOpened = React.useRef(false);
  React.useEffect(() => {
    if (autoOpenAuth && !autoOpened.current && !authMode) {
      autoOpened.current = true;
      setParams(
        (prev) => {
          prev.set("auth", "signin");
          return prev;
        },
        { replace: true },
      );
    }
  }, [autoOpenAuth, authMode, setParams]);

  return (
    <div className="lp">
      <nav className="lpnav">
        <div className="wrap navrow">
          <img className="brandimg" src={logoHorizontal} alt="Scheda" />
          <div className="navmid">
            <a href="#product">Product</a>
            <a href="#pricing">Pricing</a>
            <a href="#security">Security</a>
          </div>
          <div className="navlinks">
            {/* The language switcher is the apex's, unchanged, and it is here for a reason
                beyond parity: detection runs localStorage -> device region -> navigator, so a
                visitor in Denmark resolves to `da` whatever their browser says. This page's
                copy is still hardcoded English, so that visitor used to meet an English page
                and a Danish auth modal with no way to reconcile the two. Until the copy is
                keyed (its own story), this control and the one in the modal are the only
                way out of that mismatch. */}
            <LanguageSwitcher className="langsw" />
            {/* One door. Sign up and Log in both led to the same place, and every other
                CTA on the page already funnels here. Sign-up itself is not lost: the modal
                carries it as "New here? Create an account". */}
            <button type="button" className="btn cta" onClick={() => openAuth("signin")}>
              Log in
            </button>
          </div>
        </div>
      </nav>

      {/* 1. ORIGIN — live copy, new look. SchedaOrigin stays a div inside the section
          (guarded in marketing.test.tsx); the band ground is an explicit class now. */}
      <section className="band ochre rule-brass origin-band">
        <SchedaOrigin onTry={() => openAuth("signup")} onLogIn={() => openAuth("signin")} />
      </section>

      {/* 2. PRODUCT — content reworked: one panel, the three ways moving on the left,
          the co-pilot chat playing on the right. */}
      <section className="band panel rule-pewter" id="product">
        <div className="wrap">
          <div className="hgroup" style={{ maxWidth: "62ch" }}>
            <p className="eyebrow float">Product</p>
            <h2 className="float d1" style={{ marginTop: "10px" }}>Ways to get booked</h2>
          </div>

          <div className="duo">
            <WaysCarousel labels={["Ad-hoc", "Rolling", "Poll"]}>
              {[
                <React.Fragment key="adhoc">
                  <div className="top">
                    <h3>Ad-hoc appointments</h3>
                    <p><strong>A specific time window</strong> with a definite expiry link.</p>
                  </div>
                  <div className="art">
                    <span className="miniurl">/patricia/intro-call</span>
                    <div className="dow"><span>MON</span><span>TUE</span><span>WED</span><span>THU</span><span>FRI</span><span>SAT</span><span>SUN</span></div>
                    <div className="days">
                      <b className="open">12</b><b className="on">13</b><b className="open">14</b><b className="open">15</b><b className="open">16</b><b className="out">17</b><b className="out">18</b>
                    </div>
                    <div className="slotlist" style={{ marginTop: "10px" }}>
                      <div className="slot">13:00 <span className="tag">30 min</span></div>
                      <div className="slot gone">13:30 <span className="tag">taken</span></div>
                      <div className="slot on">14:30 <span className="tag">chosen</span></div>
                      <div className="slot">15:00 <span className="tag">30 min</span></div>
                      <div className="slot">15:30 <span className="tag">30 min</span></div>
                    </div>
                    <p className="artcap">Mon 12 to Fri 16 October, then the link expires</p>
                  </div>
                </React.Fragment>,
                <React.Fragment key="rolling">
                  <div className="top">
                    <h3>Rolling appointments</h3>
                    <p><strong>Longer lasting,</strong> personalised booking links.</p>
                  </div>
                  <div className="art">
                    <span className="miniurl">/patricia</span>
                    <div className="hours">
                      <div className="hrow"><b>Mon</b><span>09:00–17:00</span></div>
                      <div className="hrow"><b>Tue</b><span>09:00–17:00</span></div>
                      <div className="hrow"><b>Wed</b><span>09:00–14:00</span></div>
                      <div className="hrow"><b>Thu</b><span>09:00–17:00</span></div>
                      <div className="hrow"><b>Fri</b><span>09:00–12:00</span></div>
                      <div className="hrow closed"><b>Sat</b><span>Closed</span></div>
                    </div>
                    <div className="chips">
                      <span className="chip blue">4 hours notice</span>
                      <span className="chip sun">No end date</span>
                    </div>
                    <p className="artcap">Repeating weekly hours, one link</p>
                  </div>
                </React.Fragment>,
                <React.Fragment key="poll">
                  <div className="top">
                    <h3>Poll</h3>
                    <p><strong>Reaching out to others</strong> to find matching times for an event.</p>
                  </div>
                  <div className="art">
                    <span className="miniurl">/patricia/poll/team-review</span>
                    <div className="bars">
                      <div className="b"><em>Tue 14:30</em><u style={{ width: "76%" }} /><s>6</s></div>
                      <div className="b"><em>Wed 10:00</em><u style={{ width: "48%" }} /><s>4</s></div>
                      <div className="b"><em>Thu 16:00</em><u style={{ width: "24%" }} /><s>2</s></div>
                    </div>
                    <ul className="parts" style={{ marginTop: "10px" }}>
                      <li><span className="avatar" style={{ width: "22px", height: "22px", fontSize: "var(--step--4)" }}>P</span><span className="nm">@priya</span><span className="st acc">Answered</span></li>
                      <li><span className="avatar" style={{ width: "22px", height: "22px", fontSize: "var(--step--4)" }}>T</span><span className="nm">@tom</span><span className="st acc">Answered</span></li>
                      <li><span className="avatar" style={{ width: "22px", height: "22px", fontSize: "var(--step--4)" }}>M</span><span className="nm">@maya</span><span className="st pen">Waiting</span></li>
                    </ul>
                    <div className="slotlist" style={{ marginTop: "10px" }}>
                      <div className="slot on">Tue 14:30 <span className="tag">confirm</span></div>
                    </div>
                    <p className="artcap">6 of 8 can make Tuesday</p>
                  </div>
                </React.Fragment>,
              ]}
            </WaysCarousel>

            <SchedaChat />
          </div>
        </div>
      </section>

      {/* 3. HOW IT WORKS — content reworked: each step with the screen it happens on. */}
      <section className="band rust rule-bronze" id="how">
        <div className="wrap">
          <div className="hgroup" style={{ maxWidth: "62ch" }}>
            <p className="eyebrow float">How it works</p>
            <h2 className="float d1" style={{ marginTop: "10px" }}>Three steps, start to booked</h2>
          </div>

          <div className="steps">
            <div className="step float d1">
              <div className="shead">
                <div className="n">01</div>
                <h3>You write the request</h3>
                <p>Plain language, with @ for people and a saved pattern for the hours you keep.</p>
              </div>
              <div className="sart">
                <div className="minibox">
                  <div className="box">
                    find 30 minutes with <span className="at">@priya</span> and{" "}
                    <span className="at">@tom</span> next week, afternoons, and use my{" "}
                    <span className="sig">.&#123;client hours&#125;</span>
                  </div>
                  <div className="send">Send</div>
                </div>
                <div className="chips">
                  <span className="chip blue">30 minutes</span>
                  <span className="chip teal">Afternoons</span>
                  <span className="chip sun">Client hours</span>
                </div>
                <ul className="parts" style={{ marginTop: "10px" }}>
                  <li><span className="avatar" style={{ width: "22px", height: "22px", fontSize: "var(--step--4)" }}>P</span><span className="nm">@priya</span><span className="st pen">Reachable</span></li>
                  <li><span className="avatar" style={{ width: "22px", height: "22px", fontSize: "var(--step--4)" }}>T</span><span className="nm">@tom</span><span className="st pen">Reachable</span></li>
                </ul>
              </div>
            </div>

            <div className="step float d2">
              <div className="shead">
                <div className="n">02</div>
                <h3>It settles the time</h3>
                <p>Your calendar, their calendars, and a time that clears all of them.</p>
              </div>
              <div className="sart">
                <div className="track"><div className="pulse" style={{ left: "8%" }} /><div className="lbl">asking for 3 times</div></div>
                <ul className="parts">
                  <li><span className="avatar" style={{ width: "22px", height: "22px", fontSize: "var(--step--4)" }}>P</span><span className="nm">@priya</span><span className="st acc">Agreed</span></li>
                  <li><span className="avatar" style={{ width: "22px", height: "22px", fontSize: "var(--step--4)" }}>T</span><span className="nm">@tom</span><span className="st cnt">Countered</span></li>
                  <li><span className="avatar" style={{ width: "22px", height: "22px", fontSize: "var(--step--4)" }}>M</span><span className="nm">@maya</span><span className="st pen">Waiting</span></li>
                </ul>
                <div className="rounds">
                  <span className="r done">1</span><span className="r now">2</span><span className="r">3</span>
                  <span>Round 1 offered 4 times</span>
                </div>
              </div>
            </div>

            <div className="step float d3">
              <div className="shead">
                <div className="n">03</div>
                <h3>You get the result</h3>
                <p>A booking, an invitation and a record of what was decided.</p>
              </div>
              <div className="sart">
                <div className="receipt">
                  <div className="h"><i>✓</i> Booked</div>
                  <div className="when">Tuesday 13 October, 14:30–15:00</div>
                  <div className="sub">Invitations sent to 2 people. Added to your calendar.</div>
                </div>
                <div className="quick">
                  <button type="button">Open the meeting</button>
                  <button type="button">Move it</button>
                  <button type="button">Ask for a longer slot</button>
                </div>
                <ul className="parts" style={{ marginTop: "10px" }}>
                  <li><span className="nm">Invitation</span><span className="st acc">Sent</span></li>
                  <li><span className="nm">Calendar</span><span className="st acc">Updated</span></li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 4. PRICING — live copy and live tiers, new look. FAQ inside. */}
      <section className="band mist rule-pewter" id="pricing">
        <div className="wrap">
          <div className="pricehead">
            <p className="eyebrow float">Pricing</p>
            <h2 className="float d1">Simple plans that grow with you</h2>
            <p className="lede float d2">
              Start free. Upgrade when you need more links, personalisation, or agentic scheduling.
            </p>
            <div className="toggle-note float d2">
              Monthly · <b>Annual</b> <span className="save">save ~2 months</span>
            </div>
          </div>

          <div className="cmp-wrap float d2">
            <table className="cmp">
              <thead>
                <tr>
                  <th className="lead" />
                  <th>
                    <div className="tname">Free</div>
                    <div className="tprice">€0</div>
                    <div className="tper">forever</div>
                    <button type="button" className="btn ghost" onClick={() => openAuth("signup")}>Start for free</button>
                  </th>
                  <th>
                    <div className="tname">Basic</div>
                    <div className="tprice talk">Free</div>
                    <div className="tper">while in preview</div>
                    <button type="button" className="btn ghost" onClick={() => openAuth("signup")}>Get started</button>
                  </th>
                  <th className="feat">
                    <span className="badge-pop">Most popular</span>
                    <div className="tname" style={{ color: "var(--rust)" }}>Pro · Pro teams</div>
                    <div className="tprice">&nbsp;</div>
                    <div className="tper">beta</div>
                    <button type="button" className="btn cta" onClick={() => openAuth("signup")}>Join the beta</button>
                  </th>
                  <th>
                    <div className="tname">Enterprise</div>
                    <div className="tprice talk">Let's talk</div>
                    <div className="tper">&nbsp;</div>
                    <a href="mailto:hello@delgentic.com" className="btn ghost">Talk to us</a>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr className="grp"><td colSpan={5}>Booking links</td></tr>
                {/* Availability patterns, not "ranges". A pattern is one named set of weekly hours
                    ("Work default", "Family") that a link can be filled from; a range is a single
                    row inside one. The count sold here is patterns, and it is enforced
                    (@RequiresQuota(AVAILABILITY_PATTERNS)). Ranges-per-link stayed an internal
                    safety cap and is deliberately not sold. */}
                <tr><td className="lead">Availability patterns</td><td><Tier v="1" /></td><td><Tier v="5" /></td><td className="feat"><Tier v="Higher than Basic" /></td><td><Tier v="Higher than Basic" /></td></tr>
                <tr>
                  <td className="lead">Single-use / Ad-hoc links</td>
                  <td><Tier v="1 at a time" notes={["2 per month", "60 day date range"]} /></td>
                  <td><Tier v="5 at a time" notes={["20 per month", "6 month date range", "Audit log"]} /></td>
                  <td className="feat"><Tier v="Higher than Basic" /></td>
                  <td><Tier v="Higher than Basic" /></td>
                </tr>
                <tr>
                  <td className="lead">Rolling / long-lived links</td>
                  <td><Tier v="1 at a time" notes={["60 day date range"]} /></td>
                  <td><Tier v="5 at a time" notes={["6 month date range", "Audit log"]} /></td>
                  <td className="feat"><Tier v="Higher than Basic" /></td>
                  <td><Tier v="Higher than Basic" /></td>
                </tr>
                <tr>
                  <td className="lead">Polls</td>
                  <td><Tier v="1 at a time" notes={["2 per month", "60 day date range"]} /></td>
                  <td><Tier v="5 at a time" notes={["20 per month", "6 month date range", "Audit log"]} /></td>
                  <td className="feat"><Tier v="Higher than Basic" /></td>
                  <td><Tier v="Higher than Basic" /></td>
                </tr>
                <tr><td className="lead">Personalised booking link</td><td><Cell v="no" /></td><td><Cell v="yes" /></td><td className="feat"><Cell v="yes" /></td><td><Cell v="yes" /></td></tr>
                <tr><td className="lead">Embed on your own site</td><td><Cell v="no" /></td><td><Cell v="yes" /></td><td className="feat"><Cell v="yes" /></td><td><Cell v="yes" /></td></tr>
                <tr className="grp"><td colSpan={5}>Integrations</td></tr>
                <tr>
                  {/* One row for every integration we offer, so a new one (Zoom, say) is another
                      line here rather than a category heading that has to be named. */}
                  <td className="lead">
                    <ul className="ilist">
                      <li>Google Calendar / Meet</li>
                      <li>Microsoft Outlook / Teams</li>
                    </ul>
                  </td>
                  {/* One at every tier, and the page says so. A host may hold exactly one
                      calendar connection, enforced structurally by V58's unique index with
                      no tier check anywhere. Raise these the day multi-calendar ships, not
                      before — see docs/reference/pricing-page-and-plan-limits.md. */}
                  <td><Tier v="1" /></td>
                  <td><Tier v="1" /></td>
                  <td className="feat"><Tier v="1" /></td>
                  <td><Tier v="1" /></td>
                </tr>
                <tr className="grp"><td colSpan={5}>Agentic &amp; teams (beta)</td></tr>
                <tr>
                  <td className="lead">Agentic scheduling</td>
                  <td><Cell v="no" /></td>
                  <td><Cell v="no" /></td>
                  <td className="feat"><Tier v="Within the platform, or your team" /></td>
                  <td><Tier v="Platform and teams" /></td>
                </tr>
                <tr className="grp"><td colSpan={5}>Enterprise</td></tr>
                <tr><td className="lead">SSO</td><td><Cell v="no" /></td><td><Cell v="no" /></td><td className="feat"><Cell v="no" /></td><td><Cell v="yes" /></td></tr>
                <tr><td className="lead">Organisation admin page</td><td><Cell v="no" /></td><td><Cell v="no" /></td><td className="feat"><Cell v="no" /></td><td><Cell v="yes" /></td></tr>
                <tr><td className="lead">Priority support</td><td><Cell v="no" /></td><td><Cell v="no" /></td><td className="feat"><Cell v="no" /></td><td><Cell v="yes" /></td></tr>
              </tbody>
            </table>
          </div>

          <div className="faq float d3" id="faq">
            <div><p className="q">Is there a free plan?</p><p className="a">Yes. Free forever, no card needed. Upgrade only when you outgrow the limits.</p></div>
            <div><p className="q">Can I cancel anytime?</p><p className="a">Yes. No lock-in; downgrade or cancel whenever you like.</p></div>
            <div><p className="q">What happens when a link expires?</p><p className="a">It stops taking new bookings, but existing bookings and their invites are kept.</p></div>
            <div><p className="q">Do invitees need an account?</p><p className="a">No. They book, cancel and reschedule from the link, no sign-up.</p></div>
            <div><p className="q">How do I get Pro or Enterprise?</p><p className="a">Pro is in beta, so join it from the table. For Enterprise, drop us a note at hello@delgentic.com.</p></div>
          </div>
        </div>
      </section>

      {/* 5. SECURITY — live copy, new look. The "what this saves you" items live here
          now (content unchanged), above the assurance masonry. */}
      <section className="band ochre rule-bronze" id="security">
        <div className="wrap">
          <div className="hgroup" style={{ maxWidth: "62ch" }}>
            <p className="eyebrow float">Security</p>
            <h2 className="float d1" style={{ marginTop: "10px" }}>
              …that you don't get from platform native tools
            </h2>
          </div>

          <div className="saves">
            <div className="saveitem float d1">
              <span className="tick">✓</span>
              <div>
                <b>Easy navigation</b>
                <p>Everything one screen away, so you can build, share and see all your links in one place.</p>
              </div>
            </div>
            <div className="saveitem float d2">
              <span className="tick">✓</span>
              <div>
                <b>Timesaver</b>
                <p>No back and forth. Set your hours once and invitees serve themselves.</p>
              </div>
            </div>
            <div className="saveitem float d3">
              <span className="tick">✓</span>
              <div>
                <b>Easy availability exposure</b>
                <p>Show exactly when you're free, cleanly, without leaking your calendar.</p>
              </div>
            </div>
          </div>

          <Masonry
            items={SECURITY}
            render={(c, i) => (
              <div
                className={`seccard msi acc-${c.accent}`}
                style={{ animationDelay: `${Math.min(i, 9) * 55}ms` }}
              >
                <div className="ic">{c.ic}</div>
                <h3>{c.title}</h3>
                <p>{c.body}</p>
              </div>
            )}
          />
        </div>
      </section>

      {/* 6. CLOSING — live copy, new look: the brass-rimmed plate, one action. */}
      <section className="close">
        <div className="wrap">
          <div className="closecard float">
            <h2>Ready to stop the chasing?</h2>
            <p>Set your hours once and let people book themselves.</p>
            <div className="ctas">
              <button
                type="button"
                className="btn cta big"
                onClick={() => openAuth("signup")}
              >
                Set up your first appointment, free
              </button>
            </div>
          </div>
        </div>
      </section>

      <footer>
        <div className="wrap">
          <div className="rule" />
          <div className="foot">
            <span>© {new Date().getFullYear()} Scheda · Delgentic. All rights reserved.</span>
            <div className="links">
              <a href="#faq">FAQs</a>
              <a href="mailto:hello@delgentic.com">Contact</a>
              <a href="#security">Security</a>
              <a href={TERMS_OF_SERVICE_URL} target="_blank" rel="noreferrer">Terms</a>
              <a href={PRIVACY_POLICY_URL} target="_blank" rel="noreferrer">Privacy</a>
              <a href="/cookies">Cookies</a>
            </div>
          </div>
        </div>
      </footer>

      <CookieConsent />
      <AuthModal mode={authMode} from={from} onClose={closeAuth} onSwitch={openAuth} />
    </div>
  );
}
